"use server";

import { redirect } from "next/navigation";
import { eq } from "drizzle-orm";
import { db } from "@/db";
import { auditLogs, customers, domains, mailAccounts, stalwartServers, users } from "@/db/schema";
import { createMailboxSession, createSession, destroySession, ensureDefaultAdminUser, getHomePathForRole } from "@/lib/auth/session";
import { verifyPassword } from "@/lib/auth/password";
import { normalizeBaseUrl } from "@/lib/utils";

export type LoginState = {
  ok: boolean;
  message: string;
};

function getString(formData: FormData, key: string): string {
  const value = formData.get(key);
  return typeof value === "string" ? value : "";
}

function authHeader(username: string, password: string): string {
  return `Basic ${Buffer.from(`${username}:${password}`).toString("base64")}`;
}

async function validateMailboxLogin(email: string, password: string): Promise<{ ok: true; mailboxId: string } | { ok: false }> {
  const [row] = await db
    .select({ mailbox: mailAccounts, domain: domains, server: stalwartServers, customer: customers })
    .from(mailAccounts)
    .innerJoin(domains, eq(mailAccounts.domainId, domains.id))
    .innerJoin(stalwartServers, eq(domains.stalwartServerId, stalwartServers.id))
    .leftJoin(customers, eq(mailAccounts.customerId, customers.id))
    .where(eq(mailAccounts.email, email))
    .limit(1);

  if (
    !row
    || row.mailbox.status !== "active"
    || row.mailbox.locked
    || row.domain.status !== "active"
    || row.domain.locked
    || !row.server.enabled
    || row.customer?.status === "disabled"
  ) {
    return { ok: false };
  }

  try {
    const response = await fetch(`${normalizeBaseUrl(row.server.baseUrl)}/jmap/session`, {
      headers: {
        Authorization: authHeader(email, password),
        Accept: "application/json",
      },
      cache: "no-store",
    });

    if (!response.ok) {
      return { ok: false };
    }

    return { ok: true, mailboxId: row.mailbox.id };
  } catch {
    return { ok: false };
  }
}

export async function login(_state: LoginState, formData: FormData): Promise<LoginState> {
  await ensureDefaultAdminUser();

  const username = getString(formData, "username").trim().toLowerCase();
  const password = getString(formData, "password");

  if (!username || !password) {
    return { ok: false, message: "Username and password are required" };
  }

  const [user] = await db.select().from(users).where(eq(users.username, username)).limit(1);
  if (user?.isActive && verifyPassword(password, user.passwordHash)) {
    await createSession(user.id);
    await db.update(users).set({ lastLoginAt: new Date(), updatedAt: new Date() }).where(eq(users.id, user.id));
    await db.insert(auditLogs).values({
      actorType: "user",
      actorId: user.id,
      action: "auth.login",
      targetType: "user",
      targetId: user.id,
      summary: `${user.username} logged in`,
    });

    redirect(getHomePathForRole(user.role));
  }

  const mailboxLogin = username.includes("@") ? await validateMailboxLogin(username, password) : { ok: false as const };
  if (!mailboxLogin.ok) {
    return { ok: false, message: "Invalid username or password" };
  }

  await createMailboxSession(mailboxLogin.mailboxId);
  await db.insert(auditLogs).values({
    actorType: "mailbox",
    actorId: mailboxLogin.mailboxId,
    action: "auth.mailbox_login",
    targetType: "mailbox",
    targetId: mailboxLogin.mailboxId,
    summary: `${username} logged in`,
  });

  redirect("/mailbox");
}

export async function logout(): Promise<void> {
  await destroySession();
  redirect("/login");
}
